// Framework-free session store (FR-010, FR-012, FR-035; data-model §1; contracts/session-seam.md §1).
// No React import: the API layer reads it directly, React reads it through useSyncExternalStore.
import { initialsOf } from "@/school-identity/initials";

import { type AppError, isAppError } from "@/api/errors";

import type {
  SchoolIdentity,
  ServedRole,
  SessionPorts,
  SessionState,
  SessionStore,
  SignedOutReason,
  SignInPayload,
  SignOutCause,
} from "./types";

const CAUSE_OF: Record<SignedOutReason, SignOutCause> = {
  SIGNED_OUT: "USER",
  SESSION_ENDED: "SESSION_ENDED",
  NO_ACCESS: "NO_ACCESS",
  LINK_PAGE: "LINK_PAGE",
};

/** A restore failure worth retrying keeps the app restoring with the error (FR-023). */
function retryable(error: AppError): boolean {
  return (
    error.kind === "offline" ||
    error.kind === "timeout" ||
    error.kind === "server" ||
    error.kind === "unavailable" ||
    error.kind === "rateLimited"
  );
}

const SERVED: readonly ServedRole[] = ["PARENT", "SCHOOL_ADMIN"];

/** The roles this app acts on, in the order PARENT, SCHOOL_ADMIN. Others stay in `roles` only. */
export function servedRoles(roles: readonly string[]): ServedRole[] {
  return SERVED.filter((role) => roles.includes(role));
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** Shape check for a school identity (data-model §5). */
export function isSchoolIdentity(value: unknown): value is SchoolIdentity {
  return (
    isObject(value) &&
    typeof value.id === "string" &&
    typeof value.name === "string" &&
    (value.logoUrl === null || typeof value.logoUrl === "string")
  );
}

export type PayloadCheck =
  { ok: true; value: SignInPayload } | { ok: false; error: string };

/**
 * Validates an untrusted signed-in payload against the data-model §1 rules: the shape, an active
 * role that is served, and a school for an active SCHOOL_ADMIN. Initials are derived here.
 */
export function checkSignInPayload(value: unknown): PayloadCheck {
  if (!isObject(value)) return { ok: false, error: "session is not an object" };
  const { user, roles, schoolAdminSchools, activeRole } = value;
  if (!isObject(user) || typeof user.displayName !== "string") {
    return { ok: false, error: "user.displayName must be a string" };
  }
  if (!Array.isArray(roles) || !roles.every((r) => typeof r === "string")) {
    return { ok: false, error: "roles must be an array of strings" };
  }
  if (
    !Array.isArray(schoolAdminSchools) ||
    !schoolAdminSchools.every(isSchoolIdentity)
  ) {
    return { ok: false, error: "schoolAdminSchools must be a list of schools" };
  }
  if (activeRole !== "PARENT" && activeRole !== "SCHOOL_ADMIN") {
    return { ok: false, error: "activeRole must be PARENT or SCHOOL_ADMIN" };
  }
  if (!servedRoles(roles).includes(activeRole)) {
    return { ok: false, error: "activeRole is not one of the served roles" };
  }
  if (activeRole === "SCHOOL_ADMIN" && schoolAdminSchools.length === 0) {
    return {
      ok: false,
      error: "an active SCHOOL_ADMIN needs schoolAdminSchools",
    };
  }
  return {
    ok: true,
    value: {
      user: {
        displayName: user.displayName,
        initials: initialsOf(user.displayName),
      },
      roles: [...roles] as string[],
      schoolAdminSchools: schoolAdminSchools.map((s: SchoolIdentity) => ({
        id: s.id,
        name: s.name,
        logoUrl: s.logoUrl,
      })),
      activeRole,
    },
  };
}

declare const __DEV__: boolean | undefined;

export type SessionStoreOptions = {
  /** Start state; tests use it. Defaults to `restoring`. */
  initialState?: SessionState;
  /** Invalid sign-ins throw in development and sign out in production. Defaults to `__DEV__`. */
  dev?: boolean;
};

export function createSessionStore(
  ports: SessionPorts,
  options: SessionStoreOptions = {},
): SessionStore {
  let state: SessionState = options.initialState ?? {
    status: "restoring",
    error: null,
  };
  const listeners = new Set<() => void>();
  const dev = options.dev ?? (typeof __DEV__ !== "undefined" ? __DEV__ : false);

  const set = (next: SessionState) => {
    state = next;
    listeners.forEach((listener) => listener());
  };

  const clearPersonalData = async (cause: SignOutCause) => {
    for (const cleaner of ports.personalDataCleaners) {
      try {
        await cleaner(cause);
      } catch {
        // A failing cleaner must not stop the others or the sign-out itself.
      }
    }
  };

  const store: SessionStore = {
    getSnapshot: () => state,

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    async restore() {
      let restored: unknown = null;
      try {
        restored = await ports.restorer.restore();
      } catch (error) {
        if (isAppError(error) && error.code === "SESSION_ENDED") {
          set({ status: "signedOut", reason: "SESSION_ENDED" });
          return;
        }
        if (isAppError(error) && retryable(error)) {
          set({ status: "restoring", error });
          return;
        }
        restored = null;
      }
      const check = restored === null ? null : checkSignInPayload(restored);
      set(
        check?.ok
          ? { status: "signedIn", ...check.value }
          : { status: "signedOut", reason: null },
      );
    },

    async retryRestore() {
      if (state.status !== "restoring") return;
      set({ status: "restoring", error: null });
      await store.restore();
    },

    signIn(session) {
      const check = checkSignInPayload(session);
      if (!check.ok) {
        if (dev) throw new Error(`Invalid session: ${check.error}`);
        void store.signOut("SIGNED_OUT");
        return;
      }
      set({ status: "signedIn", ...check.value });
    },

    setActiveRole(role) {
      if (
        state.status !== "signedIn" ||
        !servedRoles(state.roles).includes(role)
      )
        return;
      if (role === "SCHOOL_ADMIN" && state.schoolAdminSchools.length === 0)
        return;
      if (state.activeRole !== role) set({ ...state, activeRole: role });
    },

    async signOut(
      reason: SignedOutReason = "SIGNED_OUT",
      cause?: SignOutCause,
    ) {
      set({ status: "signedOut", reason });
      await clearPersonalData(cause ?? CAUSE_OF[reason]);
    },

    onUnauthenticated() {
      if (ports.onUnauthenticated) {
        ports.onUnauthenticated(store);
      } else {
        void store.signOut("SESSION_ENDED");
      }
    },
  };

  return store;
}
